/**
 * StartFromKBModal — Select a Knowledge Base and documents to generate an ontology.
 */

import { useState, useEffect, useMemo } from 'react';
import {
  ComposedModal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Dropdown,
  Checkbox,
  Slider,
  TextInput,
  Toggle,
  InlineNotification,
  Loading,
  ProgressBar,
} from '@carbon/react';
import { useNavigate } from 'react-router-dom';
import { knowledgeBaseApi } from '@/lib/api/knowledge-base.api';
import { useKnowledgeBases } from '@/lib/query/hooks/useKnowledgeBases';
import type { KnowledgeBase, KBDocument } from '@shared/types/knowledge-base';

interface StartFromKBModalProps {
  open: boolean;
  onClose: () => void;
  /** Called after the generation request is accepted, before the modal closes. */
  onGenerationStarted?: () => void;
}

/** Estimate tokens from character count (~4 chars per token) */
const estimateTokens = (chars: number) => Math.ceil(chars / 4);

const MAX_TOKENS = 50_000;

export function StartFromKBModal({ open, onClose, onGenerationStarted }: StartFromKBModalProps) {
  const navigate = useNavigate();
  const { data: knowledgeBases, isLoading: kbLoading } = useKnowledgeBases();

  const [selectedKB, setSelectedKB] = useState<KnowledgeBase | null>(null);
  const [documents, setDocuments] = useState<KBDocument[]>([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [selectedDocIds, setSelectedDocIds] = useState<Set<string>>(new Set());
  const [nodeCount, setNodeCount] = useState(20);
  const [focus, setFocus] = useState('');
  const [multistep, setMultistep] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!open) {
      setSelectedKB(null);
      setDocuments([]);
      setSelectedDocIds(new Set());
      setNodeCount(20);
      setFocus('');
      setMultistep(false);
      setError(null);
      setGenerating(false);
    }
  }, [open]);

  // Fetch documents when a KB is selected
  useEffect(() => {
    if (!selectedKB) {
      setDocuments([]);
      setSelectedDocIds(new Set());
      return;
    }

    let cancelled = false;
    setDocsLoading(true);
    setError(null);

    knowledgeBaseApi.listDocuments(selectedKB.id).then((docs) => {
      if (!cancelled) {
        setDocuments(docs);
        setDocsLoading(false);
      }
    }).catch((err) => {
      if (!cancelled) {
        setError(err.message || 'Failed to load documents');
        setDocsLoading(false);
      }
    });

    return () => { cancelled = true; };
  }, [selectedKB]);

  // Compute token budget usage based on stripped text char count (accurate)
  // Falls back to file_size for documents converted before text_char_count was stored.
  const { totalTokens, overBudget } = useMemo(() => {
    let total = 0;
    for (const doc of documents) {
      if (selectedDocIds.has(doc.id)) {
        total += estimateTokens(doc.text_char_count ?? doc.file_size);
      }
    }
    return { totalTokens: total, overBudget: total > MAX_TOKENS };
  }, [documents, selectedDocIds]);

  const completedDocs = documents.filter((d) => d.conversion_status === 'completed');
  const canGenerate = selectedDocIds.size > 0 && !generating;

  const toggleDoc = (docId: string) => {
    setSelectedDocIds((prev) => {
      const next = new Set(prev);
      if (next.has(docId)) {
        next.delete(docId);
      } else {
        next.add(docId);
      }
      return next;
    });
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);

    try {
      const docIds = Array.from(selectedDocIds);
      const trimmedFocus = focus.trim() || undefined;
      await knowledgeBaseApi.generateFromDocuments(docIds, nodeCount, trimmedFocus);
      onGenerationStarted?.();
      onClose();
      navigate('/ontology-builder');
    } catch (err: any) {
      setError(err.message || 'Failed to generate ontology');
      setGenerating(false);
    }
  };

  return (
    <ComposedModal open={open} onClose={onClose} size="lg" style={{ maxWidth: '80vw', width: '80vw' }}>
      <ModalHeader title="Generate from Knowledge Base" />
      <ModalBody style={{ overflow: 'visible' }}>
        {generating && (
          <div style={{ marginBottom: '1rem' }}>
            <ProgressBar
              label="Generating ontology from documents..."
              status="active"
            />
          </div>
        )}

        {error && (
          <InlineNotification
            kind="error"
            title="Error"
            subtitle={error}
            lowContrast
            style={{ marginBottom: '1rem' }}
          />
        )}

        {/* Step 1: Select Knowledge Base */}
        <div style={{ marginBottom: '1.5rem' }}>
          <Dropdown
            id="kb-select"
            titleText="Knowledge Base"
            label={kbLoading ? 'Loading...' : 'Select a knowledge base'}
            items={knowledgeBases || []}
            itemToString={(item: KnowledgeBase | null) => item?.name || ''}
            onChange={({ selectedItem }: { selectedItem: KnowledgeBase | null }) => {
              setSelectedKB(selectedItem);
            }}
            disabled={generating || kbLoading}
          />
        </div>

        {/* Step 2: Select Documents */}
        {selectedKB && (
          <div style={{ marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              <h5 style={{ margin: 0 }}>
                Select documents to analyze
                {completedDocs.length > 0 && (
                  <span style={{ fontWeight: 'normal', fontSize: '0.875rem', marginLeft: '0.5rem' }}>
                    ({completedDocs.length} available)
                  </span>
                )}
              </h5>
              {!docsLoading && completedDocs.length > 0 && (
                <Checkbox
                  id="select-all-docs"
                  labelText="Select all"
                  checked={completedDocs.length > 0 && selectedDocIds.size === completedDocs.length}
                  indeterminate={selectedDocIds.size > 0 && selectedDocIds.size < completedDocs.length}
                  onChange={() => {
                    if (selectedDocIds.size === completedDocs.length) {
                      setSelectedDocIds(new Set());
                    } else {
                      setSelectedDocIds(new Set(completedDocs.map((d) => d.id)));
                    }
                  }}
                  disabled={generating}
                />
              )}
            </div>

            {docsLoading && <Loading small withOverlay={false} description="Loading documents..." />}

            {!docsLoading && completedDocs.length === 0 && (
              <InlineNotification
                kind="info"
                title="No converted documents"
                subtitle="Upload and convert documents in your Knowledge Base first."
                lowContrast
                hideCloseButton
              />
            )}

            {!docsLoading && completedDocs.length > 0 && (
              <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid var(--cds-border-subtle)', borderRadius: '4px', padding: '0.5rem' }}>
                {completedDocs.map((doc) => (
                  <div key={doc.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.25rem 0' }}>
                    <Checkbox
                      id={`doc-${doc.id}`}
                      labelText={doc.display_name || doc.filename}
                      checked={selectedDocIds.has(doc.id)}
                      onChange={() => toggleDoc(doc.id)}
                      disabled={generating}
                    />
                    <span style={{ fontSize: '0.75rem', color: 'var(--cds-text-secondary)', whiteSpace: 'nowrap', marginLeft: '0.5rem' }}>
                      ~{estimateTokens(doc.text_char_count ?? doc.file_size).toLocaleString()} tokens
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Token budget indicator */}
            {selectedDocIds.size > 0 && (
              <div style={{ marginTop: '0.75rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem' }}>
                  <span>Estimated tokens: <strong>{totalTokens.toLocaleString()}</strong></span>
                  <span style={{ color: overBudget ? 'var(--cds-support-error)' : 'var(--cds-text-secondary)' }}>
                    Limit: {MAX_TOKENS.toLocaleString()}
                  </span>
                </div>
                {overBudget && (
                  <InlineNotification
                    kind="warning"
                    title="Over the single-step token budget"
                    subtitle="Generation will use the multi-step claims/entities flow for this selection. If extracted layers are missing, run Process Layers first."
                    lowContrast
                    hideCloseButton
                    style={{ marginTop: '0.5rem' }}
                  />
                )}
              </div>
            )}
          </div>
        )}

        {/* Step 3: Concept count */}
        {selectedKB && completedDocs.length > 0 && (
          <div style={{ marginBottom: '1rem' }}>
            <Slider
              id="node-count-slider"
              labelText="Number of concepts to extract"
              min={5}
              max={50}
              step={5}
              value={nodeCount}
              onChange={({ value }: { value: number }) => setNodeCount(value)}
              disabled={generating}
            />
          </div>
        )}

        {/* Step 4: Focus + multi-step */}
        {selectedKB && completedDocs.length > 0 && (
          <div style={{ marginBottom: '1rem' }}>
            <TextInput
              id="focus-input"
              labelText="Focus (optional)"
              placeholder="e.g. battery materials, clinical trial design..."
              value={focus}
              onChange={(e) => setFocus(e.target.value)}
              disabled={generating}
            />
            <div style={{ marginTop: '1rem' }}>
              <Toggle
                id="multistep-toggle"
                labelText="Multi-step generation"
                labelA="Off"
                labelB="On"
                toggled={multistep}
                onToggle={(checked: boolean) => setMultistep(checked)}
                disabled={generating}
              />
              <p style={{ fontSize: '0.75rem', color: 'var(--cds-text-secondary)', marginTop: '0.25rem' }}>
                Recommended for large document sets: builds the ontology from extracted claims/entities
                instead of the full text. Selections over the token budget use this flow automatically.
              </p>
            </div>
          </div>
        )}
      </ModalBody>
      <ModalFooter
        primaryButtonText={generating ? 'Generating...' : 'Generate Ontology'}
        secondaryButtonText="Cancel"
        onRequestSubmit={handleGenerate}
        onRequestClose={onClose}
        primaryButtonDisabled={!canGenerate}
      >
        <></>
      </ModalFooter>
    </ComposedModal>
  );
}

// Made with Bob
